/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useCallback, useEffect, useRef, useReducer, type ReactNode } from 'react'
import { login, loginWith2FA, getMe } from '@/api/http/auth-api'
import { getAccessToken, setTokens, clearTokens, ACCESS_KEY } from '@/api/http/tokens'
import { configureAuth, refreshTokens } from '@/api/http/fetch'

type User = {
  id: number
  email: string
  first_name?: string
  last_name?: string
  full_name?: string
  avatar_url?: string
  roles: string[]
  permissions?: string[]
  license_valid?: boolean
  email_verified?: boolean
  email_verification_available?: boolean
  is_active?: boolean
  needs_approval?: boolean
}

type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; user: User }
  | { status: '2fa-required'; email: string; password: string; methods: string[] }

type AuthAction =
  | { type: 'BOOT_COMPLETE'; user: User }
  | { type: 'BOOT_ANONYMOUS' }
  | { type: 'AUTHENTICATED'; user: User }
  | { type: '2FA_REQUIRED'; email: string; password: string; methods: string[] }
  | { type: 'LOGOUT' }

function authReducer(_state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'BOOT_COMPLETE':
      return { status: 'authenticated', user: action.user }
    case 'BOOT_ANONYMOUS':
      return { status: 'anonymous' }
    case 'AUTHENTICATED':
      return { status: 'authenticated', user: action.user }
    case '2FA_REQUIRED':
      return { status: '2fa-required', email: action.email, password: action.password, methods: action.methods }
    case 'LOGOUT':
      return { status: 'anonymous' }
  }
}

const BOOT_TIMEOUT_MS = 10_000

interface AuthContextValue {
  state: AuthState
  login: (email: string, password: string) => Promise<void>
  loginWith2FA: (code: string, method: 'totp' | 'backup', trustDevice?: boolean) => Promise<void>
  logout: () => void
  fetchMe: () => Promise<User>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

let mePromise: Promise<User> | null = null

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, { status: 'loading' })
  const booted = useRef(false)

  const doLogout = useCallback(() => {
    mePromise = null
    clearTokens()
    dispatch({ type: 'LOGOUT' })
  }, [])

  const fetchMe = useCallback(async (): Promise<User> => {
    if (mePromise) return mePromise

    mePromise = (async () => {
      const data = await getMe()
      return {
        id: data.user_id,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        full_name: data.full_name,
        avatar_url: data.avatar_url,
        roles: data.roles ?? [],
        permissions: data.permissions,
        license_valid: data.license_valid,
        email_verified: data.email_verified,
        email_verification_available: data.email_verification_available,
        is_active: data.is_active,
        needs_approval: data.needs_approval,
      }
    })()

    try {
      return await mePromise
    } finally {
      mePromise = null
    }
  }, [])

  const refreshUser = useCallback(async () => {
    const user = await fetchMe()
    dispatch({ type: 'AUTHENTICATED', user })
  }, [fetchMe])

  const boot = useCallback(async () => {
    const token = getAccessToken()
    if (!token) {
      dispatch({ type: 'BOOT_ANONYMOUS' })
      return
    }

    const timeoutId = setTimeout(() => {
      if (!booted.current) {
        dispatch({ type: 'BOOT_ANONYMOUS' })
      }
    }, BOOT_TIMEOUT_MS)

    try {
      const user = await fetchMe()
      clearTimeout(timeoutId)
      booted.current = true
      dispatch({ type: 'BOOT_COMPLETE', user })
    } catch {
      try {
        await refreshTokens()
        const user = await fetchMe()
        clearTimeout(timeoutId)
        booted.current = true
        dispatch({ type: 'BOOT_COMPLETE', user })
      } catch {
        clearTimeout(timeoutId)
        booted.current = true
        dispatch({ type: 'BOOT_ANONYMOUS' })
      }
    }
  }, [fetchMe])

  useEffect(() => {
    configureAuth(doLogout)
    boot()

    const onStorage = (e: StorageEvent) => {
      if (e.key === ACCESS_KEY && !e.newValue) {
        doLogout()
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [boot, doLogout])

  useEffect(() => {
    if (state.status !== 'authenticated') return

    const heartbeat = async () => {
      try {
        await refreshTokens()
      } catch {
        // silent — session still valid, refresh will retry
      }
    }

    const interval = setInterval(heartbeat, 30 * 60 * 1000)
    return () => clearInterval(interval)
  }, [state.status])

  const doLogin = useCallback(async (email: string, password: string) => {
    const resp = await login({ email, password })

    if (resp['2fa_required']) {
      dispatch({ type: '2FA_REQUIRED', email, password, methods: resp['2fa_methods'] ?? ['totp'] })
      return
    }

    setTokens(resp.access_token, resp.refresh_token)
    const user = await fetchMe()
    dispatch({ type: 'AUTHENTICATED', user })
  }, [fetchMe])

  const doLogin2FA = useCallback(async (code: string, method: 'totp' | 'backup', trustDevice = false) => {
    if (state.status !== '2fa-required') return

    const resp = await loginWith2FA({
      email: state.email,
      password: state.password,
      code,
      method,
      trust_device: trustDevice,
    })

    setTokens(resp.access_token, resp.refresh_token)
    const user = await fetchMe()
    dispatch({ type: 'AUTHENTICATED', user })
  }, [state, fetchMe])

  return (
    <AuthContext.Provider
      value={{ state, login: doLogin, loginWith2FA: doLogin2FA, logout: doLogout, fetchMe, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
